// Relative imports (not `@/`) so bun can test this module.
import { axialHexDistance } from '../hexmap';
import type { RuleSet } from '../rules/schema';
import {
  buildingMaxHp,
  type Cell,
  type EntityId,
  fromMilli,
  gatherResource,
  type SimContext,
  type SimState,
  type Unit,
  unitMaxHp,
  unitRank,
} from '../sim';
import type { WorkerIntent } from './workerIntent';

/**
 * What the unit info panel says about a lone selected unit (pure, no React):
 * its current task and target, what its rank and position apply, and the
 * rank it is heading for. Fog-safe by construction: a task is only described
 * for the viewer's own units, and a target's name, distance and hp only when
 * the target's tile is in sight (`seen`), else just the verb.
 */

/** Dot colour of the task line: red fight, green work, grey nothing. */
export type TaskTone = 'fight' | 'work' | 'idle';

export interface UnitTask {
  tone: TaskTone;
  /** "Attacking", "Gathering food", "Heading to 3,-2", "Idle". */
  verb: string;
  /** The target's name ("Warrior", "Barracks"); only when seen. */
  target?: string;
  /** Where it works ("Farm (4/5)", "Forest"), after the verb. */
  place?: string;
  /** Tiles to the target or destination. */
  distance?: number;
  /** The target's hp against its max, when it is a seen unit or building. */
  targetHp?: { hp: number; max: number };
  /** The unit or building aimed at, for pointing at it on the map. */
  targetId?: EntityId;
}

export interface UnitTaskInput {
  state: SimState;
  ctx: SimContext;
  viewer: string;
  unit: Unit;
  /** Whether the viewer can see a tile right now. */
  seen: (cell: Cell) => boolean;
  /** This tick's `engagements` (attacker → target) and `healings` (healer → patient). */
  engaged: ReadonlyMap<EntityId, EntityId>;
  heals: ReadonlyMap<EntityId, EntityId>;
  /** The worker's job from `intentReader`: its verb and place say the job, as the Workers line does. */
  intent?: WorkerIntent | null;
}

const whole = (milli: number) => Math.ceil(fromMilli(milli));

/** Name, tile and hp of a unit or building target, as far as the viewer may know it. */
function describeTarget(
  input: UnitTaskInput,
  id: EntityId
): Pick<UnitTask, 'target' | 'distance' | 'targetHp' | 'targetId'> {
  const { state, ctx, unit, seen } = input;
  const u = state.units[id];
  const b = state.buildings[id];
  const entity = u ?? b;
  if (!entity || u?.inside || !seen(entity.pos)) return {};
  const distance = axialHexDistance(unit.pos, entity.pos);
  if (u) {
    return {
      target: ctx.rules.units[u.type]?.name ?? u.type,
      distance,
      targetId: id,
      targetHp: { hp: whole(u.hp), max: whole(unitMaxHp(ctx.rules, u)) },
    };
  }
  return {
    target: ctx.rules.buildings[b.type]?.name ?? b.type,
    distance,
    targetId: id,
    targetHp: { hp: whole(b.hp), max: whole(buildingMaxHp(state, ctx.rules, b)) },
  };
}

/**
 * The unit's current task, or null for a unit that isn't the viewer's (what
 * a rival is doing is not for the panel to say).
 */
export function unitTask(input: UnitTaskInput): UnitTask | null {
  const { state, ctx, viewer, unit, engaged, heals, intent } = input;
  if (unit.owner !== viewer) return null;
  const { rules } = ctx;

  const hit = engaged.get(unit.id);
  if (hit) return { tone: 'fight', verb: 'Attacking', ...describeTarget(input, hit) };
  const order = unit.order;
  if (order?.kind === 'attack') {
    return { tone: 'fight', verb: 'Closing in on', ...describeTarget(input, order.targetId) };
  }
  const patient = heals.get(unit.id);
  if (patient) {
    const verb = rules.units[unit.type]?.mechanical ? 'Repairing' : 'Healing';
    return { tone: 'work', verb, ...describeTarget(input, patient) };
  }
  if (order?.kind === 'build') {
    const site = state.buildings[order.buildingId];
    const name = site ? (rules.buildings[site.type]?.name ?? site.type) : 'a site';
    const at = site ? axialHexDistance(unit.pos, site.pos) : 0;
    return {
      tone: 'work',
      verb: at === 0 ? `Building ${name}` : `Going to build ${name}`,
      ...(at > 0 ? { distance: at } : {}),
    };
  }
  if (order?.kind === 'found') {
    return {
      tone: 'work',
      verb: 'Heading to found a city',
      distance: axialHexDistance(unit.pos, order.target),
    };
  }
  // A worker's job reads as the Workers line says it (Idle / Moving fall through to the path).
  if (intent && intent.verb !== 'Idle' && intent.verb !== 'Moving') {
    return {
      tone: 'work',
      verb: intent.verb,
      ...(intent.where ? { place: intent.where } : {}),
    };
  }
  const haul = unit.auto?.phase === 'haul' || (order?.kind === 'gather' && order.haul === true);
  if (haul) return { tone: 'work', verb: 'Hauling a load to a city' };
  const resource = gatherResource(state, ctx, unit);
  if (resource) {
    const where = unit.inside ? state.buildings[unit.inside] : undefined;
    return {
      tone: 'work',
      verb: `Gathering ${(rules.resources[resource]?.name ?? resource).toLowerCase()}`,
      ...(where ? { place: rules.buildings[where.type]?.name ?? where.type } : {}),
    };
  }
  // An automated scout is exploring wherever its path leads.
  if (unit.auto?.phase === 'flee') return { tone: 'work', verb: 'Fleeing danger' };
  if (unit.auto && rules.units[unit.type]?.automate === 'explore') {
    return { tone: 'work', verb: unit.auto.phase === 'patrol' ? 'Patrolling' : 'Exploring' };
  }
  const path = unit.path;
  const destination = path?.length ? path[path.length - 1] : undefined;
  if (destination) {
    return {
      tone: 'work',
      verb: `Heading to ${destination.q},${destination.r}`,
      distance: axialHexDistance(unit.pos, destination),
    };
  }
  if (unit.auto) return { tone: 'work', verb: 'Looking for work' };
  if (unit.inside) {
    const b = state.buildings[unit.inside];
    return { tone: 'idle', verb: `Inside ${rules.buildings[b?.type ?? '']?.name ?? 'a building'}` };
  }
  return { tone: 'idle', verb: 'Idle' };
}

/** "Attacking Warrior · 2 tiles · 28/45 hp" from the pieces of a task. */
export function taskText(task: UnitTask): string {
  const parts = [task.target ? `${task.verb} ${task.target}` : task.verb];
  if (task.place) parts.push(task.place);
  if (task.distance !== undefined && task.distance > 0) {
    parts.push(`${task.distance} ${task.distance === 1 ? 'tile' : 'tiles'}`);
  }
  if (task.targetHp) parts.push(`${task.targetHp.hp}/${task.targetHp.max} hp`);
  return parts.join(' · ');
}

/** One line of the "Upgrades applied" list: where a bonus comes from and what it does. */
export interface AppliedRow {
  /** "★" for a rank, "⌂" for home defence, "⬆" for a level. */
  src: string;
  label: string;
  effect: string;
}

const pct = (v: number) => `${Number((v * 100).toFixed(1))}%`;

/**
 * What is applied to a unit: its rank's hp and damage bonuses and, while it
 * stands in its owner's territory, home defence (`pacing.homeDefence`).
 */
export function unitApplied(rules: RuleSet, unit: Unit, inOwnTerritory: boolean): AppliedRow[] {
  const rows: AppliedRow[] = [];
  const rank = unitRank(rules, unit);
  const def = rules.units[unit.type];
  if (rank > 0) {
    const v = rules.veterancy;
    rows.push({ src: '★', label: `Rank ${rank}`, effect: `+${pct(v.hpPerRank * rank)} hp` });
    if ((def?.strength ?? 0) > 0) {
      rows.push({
        src: '★',
        label: `Rank ${rank}`,
        effect: `+${pct(v.strengthPerRank * rank)} damage`,
      });
    }
  }
  if (inOwnTerritory && rules.pacing.homeDefence > 0) {
    rows.push({
      src: '⌂',
      label: 'Home defence',
      effect: `−${pct(rules.pacing.homeDefence)} damage taken`,
    });
  }
  return rows;
}

export interface RankNext {
  /** The rank xp alone reaches next. */
  rank: number;
  xpToGo: number;
  /** "+10% hp, +10% damage" that rank adds over the one before. */
  gain: string;
}

/** The next rank by xp, or null for a unit that doesn't rank up or is at the top. */
export function rankNext(rules: RuleSet, unit: Unit): RankNext | null {
  const def = rules.units[unit.type];
  if (!def?.military) return null;
  const rank = unitRank(rules, unit);
  const need = rules.veterancy.ranks[rank];
  if (need === undefined) return null;
  const v = rules.veterancy;
  const gain = [
    `+${pct(v.hpPerRank)} hp`,
    ...(def.strength > 0 ? [`+${pct(v.strengthPerRank)} damage`] : []),
  ];
  return { rank: rank + 1, xpToGo: Math.ceil(need - (unit.xp ?? 0)), gain: gain.join(', ') };
}
